import PropTypes from 'prop-types'

import { pluralize } from '@aytm/app/stats/lib/util/helpers'
import { dropOffPct } from '@aytm/app/stats/page/data-quality-report/lib/format'
import { fieldingComplete } from '@aytm/app/stats/page/data-quality-report/lib/survey-status'
import SurveyPerformanceChart from '@aytm/app/stats/page/data-quality-report/sections/SurveyPerformanceChart'
import style from '@aytm/app/stats/page/data-quality-report/styles/sections/SurveyPerformance.module.scss'

// Copy kept as JS strings so apostrophes need no escaping.
const FEEDBACK_INTRO = 'Respondents can rate their experience after completing your survey.'
const NO_CHARGE = "No charge for drop-offs — that one's on us"
// Shown in place of the chart when the drop-offs cannot be placed. The date is this release's
// production deploy: drop-off attribution is forward-only, so surveys fielded before it never
// acquire it. The lead sentence carries the emphasis — it is the part that explains the absence.
const NO_DROPOFFS_LEAD =
  'Drop-off tracking is a new capability, available for surveys launched on or after August 13, 2026.'
const NO_DROPOFFS_REST = 'Surveys launched earlier may show limited or no data here.'
// Feedback could not be back-filled and has no launch date of its own, so it states the absence
// rather than a date the reader could wait for.
const NO_FEEDBACK = 'Positive feedback is a new capability. This survey did not collect respondent ratings.'
const FEEDBACK_DETAIL =
  "You can always compare your results with the average across all the surveys on our platform. Though it can give you some insights, take it with a grain of salt as respondents' feedback might be affected by factors outside your control."

const rating = n => (n == null ? '—' : Number(n).toFixed(1))

const STARS = [0, 1, 2, 3, 4]

// Five-star rating with fractional fill: a full-color row clipped to value/max width
// is overlaid on a muted base row, so 4.5/5 paints exactly half of the last star.
const Stars = ({ value, max = 5 }) => {
  const ratio = Math.max(0, Math.min(1, (value || 0) / max))
  return (
    <span data-testid="stat-dqr-survey-performance-feedback-stars" className={style.stars}>
      <span className={style.starsBase}>
        {STARS.map(i => (
          <i key={i} className="icon-aytmfd-star" />
        ))}
      </span>
      <span className={style.starsFill} style={{ width: `${ratio * 100}%` }}>
        {STARS.map(i => (
          <i key={i} className="icon-aytmfd-star" />
        ))}
      </span>
    </span>
  )
}
Stars.propTypes = { value: PropTypes.number, max: PropTypes.number }

const ratingsLabel = n => `${n} ${pluralize(n, 'rating', 'ratings')}`

const SurveyPerformance = ({ data, surveyStatus, onOpenTermReport }) => {
  const {
    dropOff = {},
    dropOffCount = 0,
    dropOffPlaced = false,
    completionRate,
    platformAvgFeedback,
    platformAvgCompletion,
    positiveFeedbackRate,
    positiveFeedbackCount = 0,
    positiveFeedbackEnabled = true
  } = data

  // Two feedback states: a mean present (populated), or the mean withheld (fewer than the producer's
  // ratings floor, zero included) -> a distinct "not enough ratings yet" state in place of a number.
  // A survey that never asked is a third: no rate is pending, so neither figure nor stars are drawn.
  const hasMean = positiveFeedbackRate != null

  // An empty set is the norm, not an edge: a survey nobody abandoned in its body plots nothing, and
  // neither does one whose responses predate drop-off attribution. What separates them is whether the
  // drop-offs could be placed at all — the first states a true zero, the second explains the absence.
  const hasDropOffs = 0 < (dropOff.series?.length || 0)

  // A share needs a settled denominator, and while the survey is in field the population is still
  // moving — so mid-field the line states the count alone. The platform comparison goes with it: a
  // comparison is the last thing worth keeping once the figure it compares has gone.
  const hasShare = fieldingComplete(surveyStatus) && completionRate != null

  // The href stays for middle-click / copy-link; the handler drives the in-page jump (the report
  // opens in the charts state, which the DQR's own frozen state would otherwise keep hidden).
  const openTermReport = event => {
    event.preventDefault()
    onOpenTermReport()
  }

  return (
    <div data-testid="stat-dqr-survey-performance" className={style.performance}>
      <div className={style.intro}>
        <div className={style.heading}>Drop-offs and exits</div>
        {/* Unplaceable drop-offs withhold the whole line, benchmark included: a comparison is the last
            thing worth keeping once the figure it compares has gone. */}
        {dropOffPlaced ? (
          <div data-testid="stat-dqr-survey-performance-completion-compare" className={style.compare}>
            <strong>
              {dropOffCount} {pluralize(dropOffCount, 'respondent', 'respondents')}
            </strong>{' '}
            dropped from your survey
            {hasShare ? (
              <>
                {' '}
                <strong>({dropOffPct(completionRate)})</strong> | Compare to{' '}
                <strong>{dropOffPct(platformAvgCompletion)}</strong> on average on our platform
              </>
            ) : null}
          </div>
        ) : null}
        <div data-testid="stat-dqr-survey-performance-no-charge" className={style.noCharge}>
          {NO_CHARGE}
        </div>
      </div>

      {hasDropOffs ? (
        <SurveyPerformanceChart
          categories={dropOff.categories}
          series={dropOff.series}
          titles={dropOff.titles}
          highlightIndex={dropOff.highlightIndex}
        />
      ) : !dropOffPlaced ? (
        <div data-testid="stat-dqr-survey-performance-dropoff-empty" className={style.noDropOffs}>
          <strong>{NO_DROPOFFS_LEAD}</strong> {NO_DROPOFFS_REST}
        </div>
      ) : null}

      <div className={style.termText}>
        PQ outcomes live in the{' '}
        <span
          data-testid="stat-dqr-survey-performance-term-report-link"
          className={style.termLink}
          onClick={openTermReport}
        >
          Term Report
        </span>
      </div>

      <div className={style.heading}>Positive Feedback Rate</div>

      <div className={style.boxes}>
        <div className={style.feedbackBox}>
          {positiveFeedbackEnabled ? (
            <>
              {hasMean ? (
                <div data-testid="stat-dqr-survey-performance-feedback-rate" className={style.rateValue}>
                  <span data-testid="stat-dqr-survey-performance-feedback-rate-value">
                    {rating(positiveFeedbackRate)}
                  </span>
                </div>
              ) : (
                <div data-testid="stat-dqr-survey-performance-feedback-not-enough" className={style.notEnough}>
                  Not enough ratings yet
                </div>
              )}
              <Stars value={hasMean ? positiveFeedbackRate : 0} />
            </>
          ) : (
            <div data-testid="stat-dqr-survey-performance-feedback-unavailable" className={style.noFeedback}>
              {NO_FEEDBACK}
            </div>
          )}
          {0 < positiveFeedbackCount ? (
            <div data-testid="stat-dqr-survey-performance-feedback-count" className={style.ratingsCount}>
              {hasMean
                ? `Based on ${ratingsLabel(positiveFeedbackCount)}`
                : `${ratingsLabel(positiveFeedbackCount)} so far`}
            </div>
          ) : null}
          <div data-testid="stat-dqr-survey-performance-feedback-rate-avg" className={style.rateAvg}>
            <strong>{rating(platformAvgFeedback)}</strong> platform average
          </div>
        </div>

        <div className={style.infoBox}>
          <div className={style.infoTitle}>How the respondents&apos; feedback works</div>
          <p className={style.infoCopy}>{FEEDBACK_INTRO}</p>
          <p className={style.infoCopy}>{FEEDBACK_DETAIL}</p>
        </div>
      </div>
    </div>
  )
}

SurveyPerformance.displayName = 'SurveyPerformance'
SurveyPerformance.propTypes = {
  state: PropTypes.string,
  tier: PropTypes.string,
  data: PropTypes.object,
  surveyStatus: PropTypes.string,
  onOpenTermReport: PropTypes.func
}
SurveyPerformance.defaultProps = { data: {}, onOpenTermReport: () => {} }
export default SurveyPerformance
